/**
 * „Nowa blokada" (T-B2b) — a one-off stretch of time the trainer is not bookable in (R4).
 *
 * Each end is a day and an hour on the trainer's clock; `model/blockDraft` turns them into the two
 * instants the wire wants and judges them first (end after start, no earlier than a day ago, ending
 * by the close of the last day the tab reads, a note of at most 500 characters). Only the field that
 * is actually wrong is marked, and the sentence saying why stands under it, tied to it; the dialog
 * opens on the first field. The note is the trainer's own and says so. The
 * sentence under it is the canon's: clients see no free starts then, standing sessions stay, and a
 * block does not repeat.
 */

import { Dialog } from '@base-ui/react/dialog';
import { useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import surface from '~/shared/ui/dialogSurface.module.css';
import form from '~/shared/ui/formDialog.module.css';
import { CloseGlyph } from '~/shared/ui/FormGlyphs';
import { WarningGlyph } from '~/shared/ui/WarningGlyph';

import { blockProblem, MAX_BLOCK_NOTE, type BlockDraft } from '../model/blockDraft';
import { fullDate } from '../model/calendarWords';
import { lastStatableDay } from '../model/dateException';
import { minutesOfTime, paddedClock, shiftDay, wallReading } from '../model/trainerClock';
import styles from './ScheduleTab.module.css';

interface BlockDialogProps {
  readonly draft: BlockDraft | null;
  readonly nowMs: number;
  readonly zone: string;
  readonly saving: boolean;
  readonly refusal: string | null;
  readonly onEdit: (draft: BlockDraft) => void;
  readonly onSave: () => void;
  readonly onClose: () => void;
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;

export function BlockDialog({
  draft,
  nowMs,
  zone,
  saving,
  refusal,
  onEdit,
  onSave,
  onClose,
}: BlockDialogProps) {
  const { t, i18n } = useTranslation();
  const noteId = useId();
  const reasonId = useId();
  const firstField = useRef<HTMLInputElement>(null);
  const today = wallReading(nowMs, zone).day;
  const lastDay = lastStatableDay(today);
  const problem = draft === null ? null : blockProblem(draft, nowMs, zone);
  const reason =
    problem === null
      ? null
      : t(`web:booking.schedule.blockProblem.${problem}`, {
          max: MAX_BLOCK_NOTE,
          date: fullDate(lastDay, i18n.language),
        });
  // Which field the problem is in: the start for one too early, the end for an end before the
  // start or past the last day, the note for a note too long.
  const wrong: 'from' | 'to' | 'note' | null =
    problem === 'startTooEarly'
      ? 'from'
      : problem === 'rangeInvalid' || problem === 'tooFar'
        ? 'to'
        : problem === 'noteTooLong'
          ? 'note'
          : null;
  const said = (where: 'from' | 'to' | 'note') =>
    reason === null || wrong !== where ? null : (
      <p id={reasonId} className={form.fieldError}>
        <WarningGlyph className={styles.warnGlyph} />
        <span>{reason}</span>
      </p>
    );
  const marked = (where: 'from' | 'to' | 'note') =>
    wrong === where
      ? { className: String(form.inputBad), invalid: true as const, describedBy: reasonId }
      : { className: '', invalid: undefined, describedBy: undefined };

  const end = (which: 'from' | 'to') => {
    if (draft === null) return null;
    const day = which === 'from' ? draft.fromDay : draft.toDay;
    const minutes = which === 'from' ? draft.fromMinutes : draft.toMinutes;
    const mark = marked(which);
    return (
      <div className={form.field}>
        <span className={form.fieldLabel}>
          {which === 'from'
            ? t('web:booking.trainer.block.from')
            : t('web:booking.trainer.block.to')}
        </span>
        <div className={styles.whenRow}>
          <input
            ref={which === 'from' ? firstField : undefined}
            type="date"
            className={`${String(form.input)} ${String(styles.whenInput)} ${mark.className}`}
            aria-label={
              which === 'from'
                ? t('web:booking.schedule.blockFromDay')
                : t('web:booking.schedule.blockToDay')
            }
            value={day}
            min={shiftDay(today, -1)}
            max={which === 'from' ? lastDay : shiftDay(lastDay, 1)}
            aria-invalid={mark.invalid}
            aria-describedby={mark.describedBy}
            onChange={(event) => {
              if (!DAY.test(event.target.value)) return;
              onEdit(
                which === 'from'
                  ? { ...draft, fromDay: event.target.value }
                  : { ...draft, toDay: event.target.value },
              );
            }}
          />
          <input
            type="time"
            step={60}
            className={`${String(form.input)} ${String(styles.clockInput)} ${problem === 'tooFar' ? '' : mark.className}`}
            aria-label={
              which === 'from'
                ? t('web:booking.schedule.blockFromClock')
                : t('web:booking.schedule.blockToClock')
            }
            value={paddedClock(minutes)}
            aria-invalid={problem === 'tooFar' ? undefined : mark.invalid}
            aria-describedby={problem === 'tooFar' ? undefined : mark.describedBy}
            onChange={(event) => {
              const next = minutesOfTime(event.target.value);
              if (next === null) return;
              onEdit(
                which === 'from' ? { ...draft, fromMinutes: next } : { ...draft, toMinutes: next },
              );
            }}
          />
        </div>
        {said(which)}
      </div>
    );
  };

  return (
    <Dialog.Root
      open={draft !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className={surface.backdrop} />
        <Dialog.Popup
          className={`${String(surface.popup)} ${String(form.dialog)}`}
          initialFocus={firstField}
        >
          {draft === null ? null : (
            <>
              <div className={form.dialogHead}>
                <div>
                  <span className={form.eyebrow}>{t('web:booking.trainer.block.eyebrow')}</span>
                  <Dialog.Title className={form.dialogTitle}>
                    {t('web:booking.trainer.block.title')}
                  </Dialog.Title>
                </div>
                <Dialog.Close className={form.close} aria-label={t('web:booking.schedule.close')}>
                  <CloseGlyph className={form.closeGlyph} />
                </Dialog.Close>
              </div>

              <div className={styles.ends}>
                {end('from')}
                {end('to')}
              </div>

              <label className={form.field} htmlFor={noteId}>
                <span className={form.fieldLabel}>{t('web:booking.schedule.blockNoteLabel')}</span>
                <input
                  id={noteId}
                  className={`${String(form.input)} ${marked('note').className}`}
                  value={draft.note}
                  placeholder={t('web:booking.trainer.block.notePlaceholder')}
                  aria-invalid={marked('note').invalid}
                  aria-describedby={marked('note').describedBy}
                  onChange={(event) => {
                    onEdit({ ...draft, note: event.target.value });
                  }}
                />
              </label>
              {said('note')}
              <Dialog.Description className={form.note}>
                {t('web:booking.schedule.blockNote')}
              </Dialog.Description>

              {refusal === null ? null : (
                <p className={form.refusal} role="alert">
                  {refusal}
                </p>
              )}

              <div className={form.dialogActs}>
                <button type="button" className={surface.ghost} onClick={onClose}>
                  {t('web:booking.schedule.cancel')}
                </button>
                <button
                  type="button"
                  className={surface.primary}
                  aria-disabled={reason !== null || saving ? true : undefined}
                  aria-describedby={reason === null ? undefined : reasonId}
                  onClick={() => {
                    if (reason === null) onSave();
                  }}
                >
                  {saving
                    ? t('web:booking.trainer.block.saving')
                    : t('web:booking.trainer.block.save')}
                </button>
              </div>
            </>
          )}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
